import { computed, getCurrentScope, onScopeDispose, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import {
  allStepsValid,
  DEFAULT_NAMES,
  defaultWizardData,
  restoreWizardData,
  rhythmAllowsManyDays,
  stepForKey,
  stepValid,
  WIZARD_STEPS
} from '@/config/fulfillmentWizardSteps'
import { buildSetupPayload } from '@/helpers/fulfillmentWizardPayload'
import { clearDraft, readDraft, writeDraft } from '@/helpers/wizardDraft'
import api from '@/store/api'

const GENERIC_ERROR = 'Something went wrong saving this method. Nothing was created.'
const PREVIEW_ERROR = 'The dates could not be worked out. Check the answers so far.'
const PREVIEW_STEPS = ['stores', 'days', 'times']
const PREVIEW_DELAY_MS = 400
const FIRST_STEP = 'method'
const ALWAYS_OPEN = { kind: 'always', days: null }
const PREVIOUS_OPEN = { kind: 'previous', days: 7 }

const serverErrors = (error, fallback) => {
  const messages = error?.response?.data?.errors
  return Array.isArray(messages) && messages.length ? messages : [fallback]
}

const withoutStores = value => JSON.stringify({ ...value, storeIds: [] })

export function useFulfillmentWizard({ storeId = null } = {}) {
  const route = useRoute()
  const router = useRouter()
  const store = useStore()
  const draftKey = `fulfillmentWizard:${store.getters.loggedInUserId}`

  const draft = readDraft(draftKey)
  const data = reactive(restoreWizardData(draft?.data))
  const applyStoreId = () => {
    if (storeId && !data.storeIds.includes(String(storeId))) data.storeIds.push(String(storeId))
  }
  applyStoreId()

  const firstUnfinishedStep = () => WIZARD_STEPS.find(s => s.key !== 'review' && !stepValid(s.key, data))?.key
  const openingStep = requested => {
    const unfinished = firstUnfinishedStep()
    return unfinished && stepForKey(unfinished).index < stepForKey(requested).index ? unfinished : requested
  }

  const requested = stepForKey(route.params.step)?.key ?? stepForKey(draft?.stepKey)?.key ?? FIRST_STEP
  const stepKey = ref(openingStep(requested))
  const step = computed(() => stepForKey(stepKey.value) ?? WIZARD_STEPS[0])
  const timeZone = computed(() => store.state?.loggedInUser?.attributes?.time_zone || undefined)
  const valid = computed(() => stepValid(stepKey.value, data))
  const canReview = computed(() => allStepsValid(data))
  const saving = ref(false)
  const errors = ref([])

  const pristine = () => stepKey.value === FIRST_STEP && withoutStores(data) === withoutStores(defaultWizardData())
  const persist = () => {
    if (pristine()) clearDraft(draftKey)
    else writeDraft(draftKey, { data: JSON.parse(JSON.stringify(data)), stepKey: stepKey.value })
  }
  watch(data, persist, { deep: true })

  const showStep = key => {
    stepKey.value = key
    errors.value = []
    router.replace({ params: { step: key }, query: route.query })
    persist()
  }
  const go = key => {
    if (!stepForKey(key)) return
    const opening = openingStep(key)
    if (opening !== stepKey.value) showStep(opening)
  }
  const reopenUnfinished = () => go(stepKey.value)
  const next = () => {
    if (!valid.value) return
    const following = WIZARD_STEPS[step.value.index + 1]
    if (following) go(following.key)
  }
  const back = () => {
    const previous = WIZARD_STEPS[step.value.index - 1]
    if (previous) go(previous.key)
  }

  const setType = value => {
    data.type = value
    data.zone.kind = value === 'shipping' ? 'anywhere' : 'map'
    if (!data.nameEdited) data.name = DEFAULT_NAMES[value] ?? ''
  }
  const setName = value => {
    data.name = value
    data.nameEdited = true
  }
  const setRhythm = value => {
    const wasOnce = data.schedule.rhythm === 'once'
    data.schedule.rhythm = value
    if (value === 'once') data.schedule.opens = { ...ALWAYS_OPEN }
    else if (wasOnce && data.schedule.opens.kind === 'always') data.schedule.opens = { ...PREVIOUS_OPEN }
    if (!rhythmAllowsManyDays(value)) data.schedule.weekdays = data.schedule.weekdays.slice(0, 1)
  }
  const setWeekdays = days => {
    const added = days.filter(day => !data.schedule.weekdays.includes(day))
    data.schedule.weekdays = rhythmAllowsManyDays(data.schedule.rhythm) ? days : added.slice(-1)
  }

  const reset = () => {
    Object.assign(data, defaultWizardData())
    applyStoreId()
    stepKey.value = FIRST_STEP
    errors.value = []
    clearPreview()
    clearDraft(draftKey)
    router.replace({ params: { step: FIRST_STEP }, query: route.query })
  }

  const preview = ref(null)
  const previewOk = ref(false)
  const previewLoading = ref(false)
  const previewError = ref('')
  const occurrences = computed(() => preview.value?.occurrences ?? [])
  let previewTimer = null
  let previewSerial = 0

  const clearPreview = () => {
    previewSerial += 1
    clearTimeout(previewTimer)
    preview.value = null
    previewOk.value = false
    previewLoading.value = false
    previewError.value = ''
  }

  const runPreview = async () => {
    if (!PREVIEW_STEPS.every(key => stepValid(key, data))) {
      clearPreview()
      return
    }
    const serial = (previewSerial += 1)
    previewLoading.value = true
    try {
      const response = await api.call(
        'POST',
        'fulfillment_methods/setup/preview',
        { fulfillment_method_setup: buildSetupPayload(data) },
        { save: false, receive: false }
      )
      if (serial !== previewSerial) return
      preview.value = response.data.data
      previewOk.value = true
      previewError.value = ''
    } catch (error) {
      if (serial !== previewSerial) return
      previewError.value = serverErrors(error, PREVIEW_ERROR)[0]
    } finally {
      if (serial === previewSerial) previewLoading.value = false
    }
  }

  const schedulePreview = () => {
    previewOk.value = false
    clearTimeout(previewTimer)
    previewTimer = setTimeout(runPreview, PREVIEW_DELAY_MS)
  }
  watch(() => [data.storeIds, data.schedule], schedulePreview, { deep: true })
  schedulePreview()
  if (getCurrentScope()) onScopeDispose(() => clearTimeout(previewTimer))

  const submit = async () => {
    saving.value = true
    errors.value = []
    try {
      const response = await api.call('POST', 'fulfillment_methods/setup', {
        fulfillment_method_setup: buildSetupPayload(data)
      })
      clearDraft(draftKey)
      return response.data.data.id
    } catch (error) {
      errors.value = serverErrors(error, GENERIC_ERROR)
      return null
    } finally {
      saving.value = false
    }
  }

  return {
    data,
    step,
    stepKey,
    valid,
    canReview,
    timeZone,
    preview,
    previewOk,
    previewLoading,
    previewError,
    occurrences,
    next,
    back,
    go,
    reopenUnfinished,
    pristine,
    setType,
    setName,
    setRhythm,
    setWeekdays,
    reset,
    submit,
    saving,
    errors
  }
}
